import { addDays } from 'date-fns';
import { Box, ThemeProvider } from '@mui/material';
import * as styles from '@components/dataGrid/FullPageDataGrid.styles';
import { PolicySearchField, SortPolicyQueryParam } from '@models/policy';
import { usePolicySearch } from '@services/policy';
import { TableWithStore } from '@components/table/StoreTable/TableWithStore';

import { columnSelector, POLICIES_COLUMN_SELECTOR_KEY } from '@pages/Policies/policyDataGrid/PolicyDataGrid.constants';
import { QuickFiltersProps } from '@components/table/QuickFilters';
import Loading from '@components/Loading';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import type { PolicySearchRequestQuery, PolicySearchResponse } from '@lio/api-types';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { getPolicyFilters } from '@pages/Policies/policyDataGrid/policySearch/getPolicyFilters';
import { useMemo } from 'react';
import { policyTableFilterMetadataBuilder } from '@pages/Policies/policyDataGrid/policySearch/policyTableFilterMetadataBuilder';
import { useComposeTheme } from '@theme/theme';

interface IPolicySearchDataGrid {
  columns: TableGridBaseColDef[];
  isLoading?: boolean;
}

export interface INoResults {
  title: string;
  subtitle: string;
  clearFilters?: () => void;
}

type TPolicyTable = {
  items: PolicySearchResponse['items'];
  params: PolicySearchRequestQuery & { enabled?: boolean };
};

const daysFromToday120 = addDays(new Date(), 120);

const FilterPolicyQueryParam = {
  searchText: 'searchText',
  [PolicySearchField.Product]: 'offeringId',
  [PolicySearchField.UpdatedDate]: 'updatedAt',
  [PolicySearchField.ProductLabel]: 'productLabelId',
  [PolicySearchField.ProductOption]: 'productOptionId',
  [PolicySearchField.WritingCompany]: 'writingCompanyId',
  [PolicySearchField.EffectiveDate]: 'effectiveOn',
  [PolicySearchField.Underwriter]: 'underwriterId',
  [PolicySearchField.Brokerage]: 'brokerageId',
  [PolicySearchField.Producer]: 'brokerageProducerId',
  [PolicySearchField.PendingBORTransfer]: 'borTransferStatus',
  [PolicySearchField.PackageLabel]: 'packageLabel',
  [PolicySearchField.QuoteSource]: 'reportingQuoteSource',
  [PolicySearchField.Status]: 'status',
  quickFilter: (value: string) => {
    switch (value) {
      case 'eligibleForRenewal':
        return [{ key: 'renewalStatus', value: 'Eligible' }];
      case 'expiresWithin120':
        return [{ key: 'expiresOnMax', value: daysFromToday120.toISOString() }];
      case 'openSubjectivities':
        return [{ key: 'hasOpenSubjectivities', value: 'true' }];
      default:
        return [];
    }
  },
};

export const PolicySearchDataGrid = ({ columns, isLoading: isMetadataLoading }: IPolicySearchDataGrid) => {
  const userAuth = useStore((state) => state.userAuth, shallow);
  const theme = useComposeTheme('light');
  const { isInternalUser } = userAuth;

  const quickFilterGroup: QuickFiltersProps['quickFilterGroup'] = useMemo(
    () => [
      {
        queryKey: 'quickFilter',
        quickFilters: [
          {
            label: 'All',
            defaultChecked: true,
            value: 'all',
          },
          {
            label: 'Eligible For Renewal',
            value: 'eligibleForRenewal',
            hidden: !isInternalUser,
          },
          {
            label: 'Expires Within 120 days',
            value: 'expiresWithin120',
          },
          {
            label: 'Open Subjectivities',
            value: 'openSubjectivities',
          },
        ],
      } as QuickFiltersProps['quickFilterGroup'][0],
    ],
    [isInternalUser],
  );

  const filters = useMemo(() => {
    return getPolicyFilters({ isInternalUser });
  }, [isInternalUser]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ ...styles.dataGridContainer }}>
        {isMetadataLoading ? (
          <Loading />
        ) : (
          <TableWithStore<TPolicyTable>
            tableId='policies table'
            storeProps={{
              quickFilterGroup,
              tableFilterTypeMetaDataBuilder: policyTableFilterMetadataBuilder,
              columnSelectorConfig: {
                columnSelector,
                selectedTab: POLICIES_COLUMN_SELECTOR_KEY,
                defaultDisabledKey: PolicySearchField.PolicyNumber,
                hiddenFromSelectionKeys: [PolicySearchField.PolicyNumber],
                defaultHiddenColumns: {
                  modified: true,
                  policyId: true,
                  producer: true,
                  productOption: true,
                  totalPremium: true,
                  underwriter: true,
                  // An omitted key renders the column visible, so the writing-company
                  // column has to be listed here to stay opt-in (LIO-29798 req 4.5).
                  [PolicySearchField.WritingCompany]: true,
                  [PolicySearchField.QuoteOrigin]: true,
                },
              },
            }}
            tableProps={{
              ariaLabel: 'policies table',
              onLoadData: usePolicySearch,
              columns,
              isInitiating: false,
              filterConfig: {
                filterQueryParam: FilterPolicyQueryParam,
                filters,
                displayMode: 'Modal',
              },
              sortConfig: {
                sortQueryParam: SortPolicyQueryParam,
                defaultSort: {
                  field: PolicySearchField.PolicyNumber,
                  sort: 'desc',
                },
              },
              rowConfig: {
                getRowId: ({ row }) => {
                  return row?.id ?? '';
                },
              },
              sxProps: { dataGrid: styles.dataGrid },

              paginationConfig: {
                currentPage: 0,
                rowsPerPage: 25,
                pageOffset: 0,
              },
              noResultsConfig: {
                noResultsMessage: 'There are currently no policies to display',
              },
            }}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};
